import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import OrderSuccessModal from '../components/OrderSuccessModal';
import { useShop } from '../../../shared/store';
import { paymentService } from '../../../shared/services/paymentService';
import type { PaymentMethod, ShippingAddress } from '../../../shared/types';
import { convertUsdToRwf, formatRwf } from '../../../shared/utils/currency';

const fallbackPaymentMethods: PaymentMethod[] = ['card', 'cod'];

const PENDING_PAYMENT_STORAGE_KEY = 'haapa.checkout.pendingPayment';
const CHECKOUT_POD_PROJECT_STORAGE_KEY = 'haapa.checkout.podProjectId';

const persistPendingPayment = (session: unknown) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(PENDING_PAYMENT_STORAGE_KEY, JSON.stringify(session));
};

const buildFlutterwaveCheckoutUrl = (paymentUrl: string, callbackUrl: string, reference?: string): string => {
  try {
    const url = new URL(paymentUrl);
    if (!url.searchParams.get('redirect_url')) {
      url.searchParams.set('redirect_url', callbackUrl);
    }
    if (reference && !url.searchParams.get('tx_ref')) {
      url.searchParams.set('tx_ref', reference);
    }
    return url.toString();
  } catch {
    return paymentUrl;
  }
};

const resolveFlutterwaveUrl = (paymentUrl?: string, reference?: string): string | undefined => {
  if (paymentUrl) {
    return paymentUrl;
  }

  if (reference && reference.startsWith('flwlnk-')) {
    return `https://checkout.flutterwave.com/v3/hosted/pay/${reference}`;
  }

  return undefined;
};

const CheckoutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartSubtotal, currentUser, placeOrder, removeFromCart, updateCartQuantity } = useShop();
  const checkoutState =
    (location.state as { podProjectId?: string; podOrderId?: string; edition?: 'digital' | 'print' } | null) ?? null;
  const persistedPodProjectId =
    typeof window !== 'undefined' ? window.sessionStorage.getItem(CHECKOUT_POD_PROJECT_STORAGE_KEY) ?? undefined : undefined;
  const podProjectId = checkoutState?.podProjectId ?? persistedPodProjectId;
  const podOrderId = checkoutState?.podOrderId;
  const checkoutEdition = checkoutState?.edition;
  const deliveryFee = 0;

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState<PaymentMethod[]>(fallbackPaymentMethods);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(true);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    fullName: currentUser?.name ?? '',
    phone: '',
    street: '',
    city: '',
    country: '',
    postalCode: '',
  });
  const [formError, setFormError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [usdToRwfRate, setUsdToRwfRate] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPaymentMethods = async () => {
      setIsLoadingPaymentMethods(true);
      try {
        const methods = await paymentService.getMethods();
        if (isMounted) {
          setAvailablePaymentMethods(methods.length > 0 ? methods : fallbackPaymentMethods);
        }
      } catch {
        if (isMounted) {
          setAvailablePaymentMethods(fallbackPaymentMethods);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPaymentMethods(false);
        }
      }
    };

    void loadPaymentMethods();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rate = await paymentService.getCurrencyRate();
        if (!cancelled) setUsdToRwfRate(rate);
      } catch {
        // Optional - keep USD-only display if config is missing.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (availablePaymentMethods.length === 0) {
      return;
    }

    if (!availablePaymentMethods.includes(paymentMethod)) {
      setPaymentMethod(availablePaymentMethods[0]);
    }
  }, [availablePaymentMethods, paymentMethod]);

  const shippingCost = deliveryFee;

  if (cart.length === 0) {
    return (
      <section className="animate-fade-up rounded-3xl border border-orange-100 bg-white p-8 text-center shadow-lg">
        <h2 className="text-2xl font-semibold text-slate-900">{t('checkout.emptyTitle')}</h2>
        <p className="mt-2 text-sm text-slate-600">{t('checkout.emptySubtitle')}</p>
        <Link
          to="/products"
          className="mt-4 inline-flex rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          {t('checkout.returnToProducts')}
        </Link>
      </section>
    );
  }

  const handleAddressFieldChange =
    (fieldName: keyof ShippingAddress) => (event: ChangeEvent<HTMLInputElement>) => {
      setShippingAddress((previousAddress) => ({ ...previousAddress, [fieldName]: event.target.value }));
    };

  const validateAddress = (): boolean => {
    const requiredFields: (keyof ShippingAddress)[] = ['fullName', 'phone', 'street', 'city', 'country'];

    const hasEmptyRequiredField = requiredFields.some((fieldName) => !shippingAddress[fieldName].trim());
    if (hasEmptyRequiredField) {
      setFormError(t('checkout.validation.required'));
      return false;
    }

    setFormError('');
    return true;
  };

  const handleCheckoutSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateAddress()) {
      return;
    }

    setIsPlacingOrder(true);
    try {
      const callbackUrl = `${window.location.origin}/checkout/payment/callback`;

      if (paymentMethod === 'card') {
        const placedOrder = await placeOrder({
          paymentMethod,
          shippingAddress,
          podProjectId,
          podOrderId,
          redirectUrl: callbackUrl,
          callbackUrl,
          metadata: checkoutEdition ? { edition: checkoutEdition } : undefined,
        });

        const paymentUrl = placedOrder.paymentUrl;
        const paymentReference = placedOrder.paymentReference;
        const resolvedOrderId = placedOrder.id;
        const resolvedUrl = resolveFlutterwaveUrl(paymentUrl, paymentReference);

        if (!resolvedUrl) {
          throw new Error('Payment link is not ready yet. Please try again.');
        }

        persistPendingPayment({
          orderId: resolvedOrderId || undefined,
          paymentReference,
          paymentUrl,
          podProjectId,
          podOrderId,
          metadata: checkoutEdition ? { edition: checkoutEdition } : undefined,
        });

        const nextUrl = buildFlutterwaveCheckoutUrl(resolvedUrl, callbackUrl, paymentReference);
        window.location.assign(nextUrl);
        return;
      }

      const placedOrder = await placeOrder({
        paymentMethod,
        shippingAddress,
        podProjectId,
        podOrderId,
        metadata: checkoutEdition ? { edition: checkoutEdition } : undefined,
      });

      setOrderId(placedOrder.id);
      setShowSuccessModal(true);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setFormError(err.message || t('checkout.validation.serverError'));
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return (
    <>
      <section className="space-y-6">
        <header className="animate-fade-up">
          <h2 className="text-3xl font-semibold text-orange-700">{t('checkout.title')}</h2>
          <p className="mt-2 text-sm text-slate-600">{t('checkout.subtitle')}</p>
        </header>

        <div className="grid gap-5 lg:grid-cols-[2fr_1fr]">
          <form
            className="animate-fade-up space-y-5 rounded-3xl border border-orange-100 bg-white p-5 shadow-lg"
            onSubmit={handleCheckoutSubmit}
            noValidate
          >
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-orange-700">{t('checkout.shippingTitle')}</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm font-medium text-slate-700" htmlFor="shipping-full-name">
                  {t('checkout.fields.fullName')}
                  <input
                    id="shipping-full-name"
                    type="text"
                    value={shippingAddress.fullName}
                    onChange={handleAddressFieldChange('fullName')}
                    className="rounded-xl border border-orange-200 px-3 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
                <label className="grid gap-1 text-sm font-medium text-slate-700" htmlFor="shipping-phone">
                  {t('checkout.fields.phone')}
                  <input
                    id="shipping-phone"
                    type="tel"
                    value={shippingAddress.phone}
                    onChange={handleAddressFieldChange('phone')}
                    className="rounded-xl border border-orange-200 px-3 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
                <label className="grid gap-1 text-sm font-medium text-slate-700 sm:col-span-2" htmlFor="shipping-street">
                  {t('checkout.fields.street')}
                  <input
                    id="shipping-street"
                    type="text"
                    value={shippingAddress.street}
                    onChange={handleAddressFieldChange('street')}
                    className="rounded-xl border border-orange-200 px-3 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
                <label className="grid gap-1 text-sm font-medium text-slate-700" htmlFor="shipping-city">
                  {t('checkout.fields.city')}
                  <input
                    id="shipping-city"
                    type="text"
                    value={shippingAddress.city}
                    onChange={handleAddressFieldChange('city')}
                    className="rounded-xl border border-orange-200 px-3 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
                <label className="grid gap-1 text-sm font-medium text-slate-700" htmlFor="shipping-country">
                  {t('checkout.fields.country')}
                  <input
                    id="shipping-country"
                    type="text"
                    value={shippingAddress.country}
                    onChange={handleAddressFieldChange('country')}
                    className="rounded-xl border border-orange-200 px-3 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
                <label className="grid gap-1 text-sm font-medium text-slate-700 sm:col-span-2" htmlFor="shipping-postal-code">
                  {t('checkout.fields.postalCode')}
                  <input
                    id="shipping-postal-code"
                    type="text"
                    value={shippingAddress.postalCode}
                    onChange={handleAddressFieldChange('postalCode')}
                    className="rounded-xl border border-orange-200 px-3 py-2 text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-100"
                  />
                </label>
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-orange-700">{t('checkout.paymentTitle')}</h3>
              <div className="grid gap-2">
                {isLoadingPaymentMethods ? (
                  <p className="rounded-xl border border-orange-100 bg-orange-50/50 px-3 py-2 text-sm text-slate-600">
                    {t('common.loading')}
                  </p>
                ) : (
                  availablePaymentMethods.map((method) => (
                    <label
                      key={method}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm transition ${
                        paymentMethod === method
                          ? 'border-orange-500 bg-orange-50 text-orange-800'
                          : 'border-orange-200 bg-white text-slate-700 hover:border-orange-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        value={method}
                        checked={paymentMethod === method}
                        onChange={() => setPaymentMethod(method)}
                        className="h-4 w-4 accent-orange-500"
                      />
                      {t(`payment.${method}`)}
                    </label>
                  ))
                )}
              </div>
            </section>

            {formError && (
              <p role="alert" className="text-sm font-semibold text-red-600">
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={isPlacingOrder || isLoadingPaymentMethods}
              className={`w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition ${
                isPlacingOrder || isLoadingPaymentMethods
                  ? 'bg-orange-300 cursor-not-allowed'
                  : 'bg-orange-500 hover:bg-orange-600'
              }`}
            >
              {isPlacingOrder
                ? 'Processing...'
                : paymentMethod === 'card'
                  ? 'Proceed to Flutterwave (Pay in RWF)'
                  : t('checkout.placeOrder')}
            </button>
          </form>

          <aside className="animate-fade-up space-y-4 rounded-2xl border border-orange-100 bg-white p-5 shadow-sm">
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-orange-700">{t('cart.title')}</h3>
              <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                {cart.map((cartItem) => (
                  <article key={cartItem.product.id} className="rounded-xl border border-orange-100 bg-orange-50/40 p-3">
                    <div className="flex gap-3">
                      <img
                        src={cartItem.product.imageUrl}
                        alt={t(cartItem.product.nameKey)}
                        className="h-16 w-16 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-sm font-semibold text-slate-900">{t(cartItem.product.nameKey)}</p>
                        <div className="text-xs font-semibold text-orange-600">
                          <p>$ {cartItem.product.price.toFixed(2)}</p>
                          {usdToRwfRate && usdToRwfRate > 0 && (
                            <p className="text-[11px] font-semibold text-slate-500">
                              (approx. {formatRwf(convertUsdToRwf(cartItem.product.price, usdToRwfRate))})
                            </p>
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <div className="inline-flex items-center rounded-lg border border-orange-200 bg-white">
                            <button
                              type="button"
                              onClick={() =>
                                updateCartQuantity(cartItem.product.id, Math.max(1, cartItem.quantity - 1))
                              }
                              className="px-2 py-1 text-sm font-semibold text-orange-600 transition hover:bg-orange-50"
                              aria-label={t('cart.decreaseQuantity')}
                            >
                              -
                            </button>
                            <span className="min-w-8 px-2 text-center text-sm font-semibold text-orange-700">
                              {cartItem.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateCartQuantity(cartItem.product.id, cartItem.quantity + 1)}
                              className="px-2 py-1 text-sm font-semibold text-orange-600 transition hover:bg-orange-50"
                              aria-label={t('cart.increaseQuantity')}
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFromCart(cartItem.product.id)}
                            className="text-xs font-semibold text-orange-600 transition hover:text-orange-700"
                          >
                            {t('cart.remove')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <div className="border-t border-orange-100" />

            <h3 className="text-lg font-semibold text-orange-700">{t('checkout.orderSummary')}</h3>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-slate-500">{t('cart.subtotal')}</dt>
                <dd className="font-semibold text-orange-700">$ {cartSubtotal.toFixed(2)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-slate-500">{t('cart.shipping')}</dt>
                <dd className="font-semibold text-orange-700">
                  $ {shippingCost.toFixed(2)}
                </dd>
              </div>
              <div className="flex items-center justify-between border-t border-orange-100 pt-2">
                <dt className="text-slate-700">{t('cart.total')}</dt>
                <dd className="text-base font-bold text-orange-700">$ {(cartSubtotal + shippingCost).toFixed(2)}</dd>
              </div>
            </dl>
          </aside>
        </div>
      </section>

      <OrderSuccessModal
        isOpen={showSuccessModal}
        orderId={orderId}
        onClose={() => setShowSuccessModal(false)}
        onGoToDashboard={() => {
          setShowSuccessModal(false);
          void navigate('/dashboard/orders');
        }}
      />
    </>
  );
};

export default CheckoutPage;
